import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { CLIENT_ORDER_STATUSES, type ClientOrderStatus } from "@threadheads/shared";
import {
  CLIENT_ORDER_STATUS_CHIP_CLASS,
  CLIENT_ORDER_STATUS_LABEL_KEY,
} from "../utils/status-style";

export type ClientOrderStatusFilterValue = ClientOrderStatus | "all";

interface ClientOrderStatusFilterProps {
  value: ClientOrderStatusFilterValue;
  onChange: (next: ClientOrderStatusFilterValue) => void;
  className?: string;
}

/** Filter order matches the ladder: "All orders" then the 6 status steps (5
 *  ladder + cancelled terminal). Each entry carries its own i18n key so the
 *  labels resolve through the shared `clientOrders` namespace. */
const OPTIONS: Array<{ key: ClientOrderStatusFilterValue; labelKey: string }> = [
  { key: "all", labelKey: "filter.all" },
  ...CLIENT_ORDER_STATUSES.map((status) => ({
    key: status,
    labelKey: CLIENT_ORDER_STATUS_LABEL_KEY[status],
  })),
];

/**
 * BLU-660 — status filter for the client order dashboard. Wired to the list
 * RPC's `p_status`. A row of toggle chips (buttons with `aria-pressed`, grouped
 * under `role="group"` so a screen reader announces the filter set): the active
 * status uses its semantic chip fill (same colours as the row chips), inactive
 * chips are quiet outlines. Keyboard-navigable (each chip is a real button in
 * visual order) with a visible focus ring.
 */
export function ClientOrderStatusFilter({
  value,
  onChange,
  className,
}: ClientOrderStatusFilterProps) {
  const { t } = useTranslation("clientOrders");

  return (
    <div
      role="group"
      aria-label={t("filter.ariaLabel")}
      className={cn("flex flex-wrap items-center gap-1.5", className)}
    >
      {OPTIONS.map((option) => {
        const active = value === option.key;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.key)}
            className={cn(
              "inline-flex h-8 items-center rounded-md px-2.5 text-xs font-medium",
              "transition-colors duration-150 motion-reduce:transition-none",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              active
                ? option.key === "all"
                  ? "bg-primary text-primary-foreground"
                  : cn(
                      CLIENT_ORDER_STATUS_CHIP_CLASS[option.key],
                      "ring-1 ring-inset ring-black/5",
                    )
                : "bg-transparent text-muted-foreground ring-1 ring-inset ring-border hover:bg-muted/60 hover:text-foreground",
            )}
          >
            {t(option.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
